import { useEffect, useState } from "react";
import { site } from "../lib/site";

type Props = { minDurationMs?: number; onDone?: () => void };

export function Loader({ minDurationMs = 1800, onDone }: Props) {
  const [phase, setPhase] = useState<"running" | "fading" | "gone">("running");

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const runTime = reduce ? 300 : minDurationMs;
    const fadeMs = 400;

    const t1 = window.setTimeout(() => setPhase("fading"), runTime);
    const t2 = window.setTimeout(() => {
      setPhase("gone");
      onDone?.();
    }, runTime + fadeMs);

    document.documentElement.style.overflow = "hidden";
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      document.documentElement.style.overflow = "";
    };
  }, [minDurationMs, onDone]);

  if (phase === "gone") return null;

  return (
    <div
      className={`fixed inset-0 z-[60] grid place-items-center bg-gradient-to-br from-brand-50 via-white to-brand-100 transition-opacity duration-[400ms] ${
        phase === "fading" ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
      role="status"
      aria-live="polite"
      aria-label="Chargement en cours"
    >
      <div className="flex flex-col items-center gap-6">
        <LogoLoaderIcon size={180} />

        <div className="flex flex-col items-center gap-1 animate-fade-in">
          <div className="font-display font-bold text-brand-900 text-2xl tracking-wide">
            {site.name}
          </div>
          <div className="text-xs text-ink-muted">
            Nettoyage professionnel &middot; Vichy
          </div>
        </div>

        <div className="loader-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </div>

      <style>{loaderCss}</style>
    </div>
  );
}

type IconProps = { size?: number; className?: string };

export function LogoLoaderIcon({ size = 160, className }: IconProps) {
  return (
    <div
      className={`logo-loader ${className ?? ""}`}
      style={{ ["--logo-loader-size" as string]: `${size}px` }}
    >
      <div className="logo-loader__sweep">
        <div className="logo-loader__flex">
          <img
            className="logo-loader__mark"
            src="/logo-mark.png"
            alt=""
            width={282}
            height={349}
            decoding="async"
          />
        </div>
      </div>

      <div className="logo-loader__ground" aria-hidden="true">
        <span className="logo-loader__speck" style={{ ["--d" as string]: "0s" }} />
        <span className="logo-loader__speck" style={{ ["--d" as string]: "0.28s" }} />
        <span className="logo-loader__speck" style={{ ["--d" as string]: "0.56s" }} />
        <span className="logo-loader__speck" style={{ ["--d" as string]: "0.84s" }} />
        <span className="logo-loader__speck" style={{ ["--d" as string]: "1.12s" }} />
      </div>
    </div>
  );
}

const loaderCss = `
.logo-loader {
  --logo-loader-size: 160px;
  --logo-loader-sweep-duration: 1.6s;
  --logo-loader-flex-lag: calc(var(--logo-loader-sweep-duration) * 0.08);
  position: relative;
  width: var(--logo-loader-size);
  height: calc(var(--logo-loader-size) * 1.15);
  display: grid;
  grid-template-rows: 1fr auto;
  justify-items: center;
}

/* Rotation principale : tout le logo pivote autour du haut (comme le haut
   d'un manche de balai). Easing sinusoïdal pour un mouvement de pendule. */
.logo-loader__sweep {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: start center;
  transform-origin: 50% 6%;
  animation: logo-sweep var(--logo-loader-sweep-duration)
    cubic-bezier(0.37, 0, 0.63, 1) infinite;
  will-change: transform;
}
@keyframes logo-sweep {
  0%, 100% { transform: rotate(-9deg); }
  50%      { transform: rotate(9deg);  }
}

/* Flexion secondaire : le bas du logo (le "balai") pivote autour d'une
   charnière virtuelle au niveau du ventre du cygne, avec un petit retard.
   Donne l'impression d'un manche qui fléchit à chaque balayage. */
.logo-loader__flex {
  display: grid;
  place-items: start center;
  width: 100%;
  transform-origin: 50% 45%;
  animation: logo-flex var(--logo-loader-sweep-duration)
    cubic-bezier(0.37, 0, 0.63, 1) infinite;
  animation-delay: var(--logo-loader-flex-lag);
  will-change: transform;
}
@keyframes logo-flex {
  0%, 100% { transform: rotate(-4deg); }
  50%      { transform: rotate(4deg);  }
}

.logo-loader__mark {
  width: 88%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 8px 14px rgba(22, 163, 74, 0.2));
  display: block;
}

/* Sol + poussières sous le logo */
.logo-loader__ground {
  position: relative;
  width: 82%;
  height: 10px;
  margin-top: 2px;
}
.logo-loader__ground::before {
  content: "";
  position: absolute;
  inset: 4px 0 auto 0;
  height: 1.5px;
  background: #94a3b8;
  opacity: 0.35;
  border-radius: 1px;
}

.logo-loader__speck {
  position: absolute;
  bottom: 1px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #64748b;
  opacity: 0;
  animation: logo-dust var(--logo-loader-sweep-duration) ease-in-out infinite;
  animation-delay: var(--d, 0s);
}
.logo-loader__speck:nth-child(1) { left: 12%; width: 4px; height: 4px; }
.logo-loader__speck:nth-child(2) { left: 30%; width: 3px; height: 3px; }
.logo-loader__speck:nth-child(3) { left: 50%; width: 3.5px; height: 3.5px; }
.logo-loader__speck:nth-child(4) { left: 70%; width: 2.5px; height: 2.5px; }
.logo-loader__speck:nth-child(5) { left: 86%; width: 3px; height: 3px; }

@keyframes logo-dust {
  0%   { opacity: 0;    transform: translateX(-5px) translateY(0); }
  25%  { opacity: 0.6;  transform: translateX(-1px) translateY(-1px); }
  60%  { opacity: 0.45; transform: translateX(4px)  translateY(-3px); }
  85%  { opacity: 0.15; transform: translateX(7px)  translateY(-2px); }
  100% { opacity: 0;    transform: translateX(9px)  translateY(0); }
}

.loader-dots {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  height: 10px;
}
.loader-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0891B2;
  opacity: 0.3;
  animation: loader-dot 1.2s ease-in-out infinite;
}
.loader-dots span:nth-child(2) { animation-delay: 0.15s; }
.loader-dots span:nth-child(3) { animation-delay: 0.3s;  }
@keyframes loader-dot {
  0%, 100% { opacity: 0.25; transform: translateY(0); }
  50%      { opacity: 1;    transform: translateY(-3px); }
}

@media (prefers-reduced-motion: reduce) {
  .logo-loader__sweep,
  .logo-loader__flex,
  .logo-loader__speck,
  .loader-dots span {
    animation: none;
  }
  .logo-loader__sweep,
  .logo-loader__flex { transform: none; }
  .logo-loader__speck { opacity: 0.3; transform: none; }
  .loader-dots span   { opacity: 0.6; }
}
`;
